// AdminBlog.jsx
import { useEffect, useState } from "react";
import { db, storage } from "../firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function AdminBlog() {
  // ===== STATES =====
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Form fields
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState(null);

  // Validation errors
  const [errors, setErrors] = useState({});

  // Notifications
  const [notification, setNotification] = useState(null);

  // ===== HELPERS =====
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const resetForm = () => {
    setTitle("");
    setSlug("");
    setExcerpt("");
    setContent("");
    setImageFile(null);
    setErrors({});
    setEditingId(null);
  };

  // ===== FETCH BLOGS =====
  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const q = query(collection(db, "blogs"), orderBy("date", "desc"));
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        date: d.data().date?.toDate().toLocaleDateString("en-GB"),
      }));
      setBlogs(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // ===== VALIDATION =====
  const validate = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "Please enter title";
    if (!slug.trim()) newErrors.slug = "Please enter slug";
    if (!excerpt.trim()) newErrors.excerpt = "Please enter excerpt";
    if (!content.trim()) newErrors.content = "Please enter content";
    if (!editingId && !imageFile) newErrors.image = "Please upload image";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ===== SUBMIT =====
  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      let imageUrl = "";

      if (imageFile) {
        const imgRef = ref(storage, `blogs/${Date.now()}_${imageFile.name}`);
        const snap = await uploadBytes(imgRef, imageFile);
        imageUrl = await getDownloadURL(snap.ref);
      }

      if (editingId) {
        const existing = blogs.find((b) => b.id === editingId);
        await updateDoc(doc(db, "blogs", editingId), {
          title,
          slug,
          excerpt,
          content,
          image: imageUrl || existing.image,
        });
        showNotification("Blog updated successfully");
      } else {
        await addDoc(collection(db, "blogs"), {
          title,
          slug,
          excerpt,
          content,
          image: imageUrl,
          date: serverTimestamp(),
        });
        showNotification("Blog added successfully");
      }

      setShowForm(false);
      resetForm();
      fetchBlogs();
    } catch (e) {
      console.error(e);
      showNotification("Something went wrong", "error");
    }
  };

  // ===== EDIT =====
  const handleEdit = (blog) => {
    setEditingId(blog.id);
    setTitle(blog.title);
    setSlug(blog.slug);
    setExcerpt(blog.excerpt);
    setContent(blog.content);
    setImageFile(null);
    setErrors({});
    setShowForm(true);
  };

  // ===== DELETE =====
  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, "blogs", deleteId));
      setDeleteId(null);
      fetchBlogs();
      showNotification("Blog deleted");
    } catch (e) {
      console.error(e);
    }
  };

  // ===== UI =====
  return (
    <>
      {/* Notification */}
      {notification && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className={`bg-white border-2 px-6 py-4 rounded-lg shadow text-center ${
              notification.type === "error"
                ? "border-red-500"
                : "border-brand-gold"
            }`}
          >
            {notification.message}
          </div>
        </div>
      )}

      <div className="p-4 space-y-8">
        <h1 className="text-3xl font-bold text-brand-dark text-center">
          Admin Blog Manager
        </h1>
        {/* Add Button */}
        <div className="text-center">
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="bg-brand-gold text-white px-6 py-2 rounded-md btn-primary"
          >
            Add New Blog
          </button>
        </div>

        {/* FORM */}
        {showForm && (
          <div className="bg-white p-6 rounded-lg shadow max-w-xl mx-auto space-y-4">
            <Input
              label="Title *"
              value={title}
              setValue={setTitle}
              error={errors.title}
            />
            <Input
              label="Slug *"
              value={slug}
              setValue={setSlug}
              error={errors.slug}
            />
            <Textarea
              label="Excerpt *"
              value={excerpt}
              setValue={setExcerpt}
              error={errors.excerpt}
            />
            <Textarea
              label="Content *"
              value={content}
              setValue={setContent}
              error={errors.content}
            />

            <div>
              <input
                type="file"
                onChange={(e) => setImageFile(e.target.files[0])}
              />
              {errors.image && (
                <p className="text-red-600 text-sm">{errors.image}</p>
              )}
            </div>

            <button
              onClick={handleSubmit}
              className="w-full bg-brand-dark text-white py-2 rounded-md"
            >
              {editingId ? "Update Blog" : "Add Blog"}
            </button>
          </div>
        )}

        {/* GRID */}
        {loading ? (
          <p className="text-center">Loading...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.map((blog) => (
              <div
                key={blog.id}
                className="bg-white rounded-lg shadow card-hover"
              >
                <img
                  src={blog.image}
                  className="h-48 w-full object-cover rounded-t-lg"
                />
                <div className="p-4 space-y-2">
                  <h2 className="font-bold text-brand-dark">{blog.title}</h2>
                  <p className="text-sm text-gray-600">{blog.excerpt}</p>

                  <div className="flex justify-between pt-2">
                    <button
                      onClick={() => handleEdit(blog)}
                      className="text-brand-gold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteId(blog.id)}
                      className="text-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* DELETE MODAL */}
        {deleteId && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded space-y-4 text-center">
              <p>Delete this blog?</p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleDelete}
                  className="bg-red-600 text-white px-4 py-1 rounded"
                >
                  Yes
                </button>
                <button
                  onClick={() => setDeleteId(null)}
                  className="bg-gray-300 px-4 py-1 rounded"
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ===== SMALL INPUT COMPONENTS ===== */

const Input = ({ label, value, setValue, error }) => (
  <div>
    <input
      className="w-full border p-2 rounded"
      placeholder={label}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
    {error && <p className="text-red-600 text-sm">{error}</p>}
  </div>
);

const Textarea = ({ label, value, setValue, error }) => (
  <div>
    <textarea
      className="w-full border p-2 rounded"
      rows="4"
      placeholder={label}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
    {error && <p className="text-red-600 text-sm">{error}</p>}
  </div>
);
